import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModelFinancialReport } from "wint-api-client/dist/types";
import { Amount } from "models/utilTypes/currencyType";
import { AccountingFormTypes } from "models/formTypes";
import { ReportHanlder, ReportProps } from "models/propTypes/accountingPropTypes";
import { NestedTableHandles } from "models/propTypes/simpleTableProps";
//#endregion
//#region components
import { DrillDownTable, useDownload, useFilterContext } from "components/common";
import useFinancialReportRow from "../hooks/useFinancialReportRow";
import useApplyExpandAllRows from "../hooks/useApplyExpandAllRows";
import useMonthlyReportDefaults from "../hooks/useMonthlyReportDefaults";
import PrintMonthlyResultReport from "./print/monthly";
import { withTagReportDefaults } from "./reportTypeSwitchFilter";
import { useFinancialReportService } from "serviceHooks";
//#endregion
import { DateHelper, ExportHelper, DimensionHelper, FinancialReportHelper } from "utils/helpers";

const IncomeStatementByTags: React.ForwardRefRenderFunction<ReportHanlder, ReportProps> = (
  { updateReportExportData, reloadKey, reload, setDisableExport },
  ref
) => {
  const financialReportService = useFinancialReportService();
  const [loading, setLoading] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<WintSuperkollAPIModelFinancialReport.FinancialReportRow[]>([]);
  const [columns, setColumns] = React.useState<string[]>([]);
  const [filter, setFilter] =
    React.useState<WintSuperkollAPIModelFinancialReport.FinancialReportMultiDimensionFilter>();
  const {
    filter: pageFilter,
    setFilter: setPageFilter,
    queryHasFilterProps,
  } = useFilterContext<AccountingFormTypes.FinancialReportFilter>();

  const dimensionTypes = useSelector((store: AppState) => store.common.dimensions.allDimensionTypes);

  const { getDefaultMonthRange } = useMonthlyReportDefaults();

  const { handleDownloadPDFRender } = useDownload();

  const tableRef = React.useRef<NestedTableHandles>(null);

  React.useImperativeHandle(ref, () => ({
    print: (title?: string, subtitle?: string, fileName?: string) => {
      handleDownloadPDFRender(
        <PrintMonthlyResultReport
          title={title}
          subtitle={subtitle}
          result={result}
          amountFormatter={convertSign}
          amountHeaderDescription={headerDescription}
          showAccountNumber={pageFilter?.showAccountNumber ?? false}
        />,
        `${fileName}.pdf`
      );
    },
    expandAll: () => tableRef.current?.expandAll(),
    collapseAll: () => tableRef.current?.collapseAll(),
  }));

  React.useEffect(() => {
    if (dimensionTypes) {
      const defaultMonthRange = getDefaultMonthRange();
      const initDimensionType = dimensionTypes?.length ? dimensionTypes[0] : undefined;
      const initDimensionTypeText = initDimensionType
        ? DimensionHelper.getDimensionTypeText(initDimensionType.type, initDimensionType.name)
        : undefined;

      const initialFilter: AccountingFormTypes.FinancialReportFilter = {
        monthRange: defaultMonthRange.monthRange,
        dimensionType: initDimensionTypeText,
        dimensionTypeId: initDimensionType?.id,
        onlyDimensionsWithTransactions: true,
        onlyActiveDimensions: true,
      };

      if (queryHasFilterProps()) setPageFilter(withTagReportDefaults(pageFilter, initialFilter));
      else setPageFilter(initialFilter, true);
    }
  }, [dimensionTypes]);

  React.useEffect(() => {
    setDisableExport(!!pageFilter?.secondDimensionTypes?.length);

    const updateFilter: WintSuperkollAPIModelFinancialReport.FinancialReportMultiDimensionFilter = {
      ...filter,
      dimensionType: pageFilter?.dimensionTypeId,
      crossDimensionTypes: pageFilter?.secondDimensionTypes?.map((sd) => sd.id as string) ?? [],
      onlyDimensionsWithTransactions: pageFilter?.onlyDimensionsWithTransactions ?? false,
      onlyActiveDimensions: pageFilter?.onlyActiveDimensions ?? false,
    };

    if (pageFilter?.monthRange?.start && pageFilter?.monthRange?.end) {
      updateFilter.startMonth = DateHelper.getYearMonthByDate(pageFilter.monthRange.start);
      updateFilter.endMonth = DateHelper.getYearMonthByDate(pageFilter.monthRange.end);
    } else {
      const defaultMonthRange = getDefaultMonthRange();
      updateFilter.startMonth = defaultMonthRange.yearMonthRange.startMonth;
      updateFilter.endMonth = defaultMonthRange.yearMonthRange.endMonth;
    }
    if (JSON.stringify(filter) !== JSON.stringify(updateFilter)) setFilter(updateFilter);
  }, [pageFilter]);

  React.useEffect(() => {
    if (filter?.dimensionType) {
      setLoading(true);
      financialReportService
        .getDimensionResultReport(filter)
        .then((res) => {
          setResult(res.rows ?? []);
          setColumns(res.columns ?? []);
        })
        .finally(() => setLoading(false));
    } else {
      setResult([]);
      setColumns([]);
    }
  }, [filter]);

  const headerStringDescription = React.useCallback((repeatIndex: number) => columns[repeatIndex], [columns]);
  const headerDescription = React.useCallback(
    (repeatIndex: number) => <>{headerStringDescription(repeatIndex)}</>,
    [headerStringDescription, columns]
  );

  const convertSign = (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) =>
    row.amount ? row.amount * -1 : row.amount;

  const formatter = (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) =>
    new Amount(undefined, convertSign(row));

  const { descriptionFormatter } = useFinancialReportRow(pageFilter?.showAccountNumber ?? false);

  React.useEffect(() => {
    const headers = ExportHelper.generateSheetHeaders(result, (_, index) => [headerStringDescription(index)]);
    const sheet = ExportHelper.generateSheetData(result, descriptionFormatter, (col) => [formatter(col).amount]);
    updateReportExportData(headers.concat(sheet));
  }, [result, headerDescription, descriptionFormatter]);

  React.useEffect(() => {
    reload();
  }, [pageFilter?.showAccountNumber]);

  useApplyExpandAllRows(tableRef, result, pageFilter?.expandAllRows);

  return (
    <DrillDownTable<
      WintSuperkollAPIModelFinancialReport.FinancialReportRow,
      WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn
    >
      key={reloadKey}
      tableRef={tableRef}
      rows={result}
      rowTextProperty={{
        key: "description",
        properties: {
          description: TranslationKeys.Common.Name,
          formatter: descriptionFormatter,
        },
      }}
      repeatProperties={{
        key: "columns",
        properties: {
          amount: {
            description: headerDescription,
            formatter: formatter,
            alignment: "right",
          },
        },
        sumNegative: (row: WintSuperkollAPIModelFinancialReport.FinancialReportRowColumn) => row.amount > 0,
      }}
      childrenKey="children"
      rowDisplayType={FinancialReportHelper.getResultReportRowDisplayType}
      loading={loading}
      showTotal={{
        key: "amount",
        valueFormatter: convertSign,
        displayFormatter: (value: number) => new Amount(undefined, value),
      }}
    />
  );
};

export default React.forwardRef(IncomeStatementByTags);
